// The client page: the whole engagement on one page, and the ONLY page for a
// client.
//
// There were briefly two — this one for the engagement, the generic record page
// for the fields and the timeline — and using it made the cost obvious: after a
// call you were on the nice page and the place to write the note was somewhere
// else, reachable only if you knew a second URL existed. /e/org_* redirects here
// now, and the timeline came with it.
//
// This is the org record shown outward (planning/crum/client-surface.md §5).
//
// **Shape: a fact strip, then tabs.** It used to be one long scroll of five
// sections, four of which hid themselves when empty — so a quiet client rendered
// as an empty screen with nothing to read and no sense of how the engagement was
// going. Two things fix that and both are here:
//
//   the strip  four numbers that ALWAYS render. A new client shows no money, no
//              milestone, no open work and never-spoke, and that is still an
//              answer. Nothing in the strip is conditional.
//   the tabs   the sections are lenses now, not a stack. The header and the strip
//              stay put above them, so switching tabs never reads as a new page.
//
// **Project-level dates only.** "Coming up" lists GROUP milestones across every
// board. Item dates here would make this a second board, which is the one thing
// this page must not become.
//
// The composer stays on Overview even though the whole thread moved to its own
// tab: filing a note after a call is what this page is for, and putting that one
// click away would undo the reason the two pages were merged.

import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { AddRecord } from '../components/AddRecord'
import { AsideField } from '../components/AsideField'
import {
  Avatar,
  Button,
  ConfirmAction,
  EditableText,
  EmptyState,
  Section,
} from '../components/common'
import { EntryThread } from '../components/EntryThread'
import { Icon } from '../components/Icon'
import { AsideBlock, Fact, Page, SubTabs } from '../components/Page'
import { useCreateEntity } from '../components/useCreate'
import { api, clientPageUrl } from '../lib/api'
import { formatDate, money, relativeShort } from '../lib/format'
import { dueTone, KINDS, labelOf, stageTone } from '../lib/model'
import {
  artifactsForOrg,
  dealsForOrg,
  descriptionOf,
  engagementFacts,
  fieldStr,
  connectionsForOrg,
  groupsForWork,
  isBoard,
  itemsForWork,
  linkedOfKind,
  milestonesForWorks,
  projectsForOrg,
  shownCount,
  stageOf,
  titleOf,
  type EngagementFacts,
  type EntityRecord,
} from '../lib/reducer'
import { useStore } from '../lib/store'
import { EcosystemTab } from './EcosystemTab'
import { RichField } from './EntityDetail'

type TabKey =
  | 'overview'
  | 'projects'
  | 'artifacts'
  | 'deals'
  | 'people'
  | 'ecosystem'
  | 'timeline'
  | 'client'

// Every tab names exactly one record type, the same rule the top strip follows.
// "Work" was the last place the old name for a project survived, and it covered two
// types at once — which is the thing that made it read as a wrapper rather than a
// noun.
const TAB_KEYS: TabKey[] = [
  'overview',
  'projects',
  'artifacts',
  'deals',
  'people',
  'ecosystem',
  'timeline',
  'client',
]

export function ClientPage() {
  const { orgId = '' } = useParams()
  const { state, config, version, dispatch } = useStore()
  const navigate = useNavigate()
  // The tab is in the URL, not in state. It started as local state on the argument
  // that a section is a lens on one record and does not belong in the path — which
  // is still true of the PATH, and is why this is a query param. Local state was
  // wrong for a different reason: going back from a record you opened from the Work
  // tab dropped you on Overview, so every drill-down cost you your place. `?peek=`
  // already carries panel state the same way.
  const [params, setParams] = useSearchParams()
  const raw = params.get('tab') as TabKey | null
  const tab: TabKey = raw && TAB_KEYS.includes(raw) ? raw : 'overview'
  const setTab = (next: TabKey) => {
    const p = new URLSearchParams(params)
    if (next === 'overview') p.delete('tab')
    else p.set('tab', next)
    // A push, not a replace: switching tab is somewhere you went, and back should
    // undo it.
    setParams(p)
  }
  void version

  const org = state.entities[orgId]
  if (!org) {
    return (
      <Page title="Not found">
        <EmptyState icon="Building2" title="No such client" hint="It may have been archived." />
      </Page>
    )
  }

  /** The team's own record. Read-only here: which org is us is set in
   *  Settings → Who can sign in, because it decides who can sign in. */
  const isUs = fieldStr(org, 'state') === 'internal'

  const projects = projectsForOrg(state, orgId)
  const artifacts = artifactsForOrg(state, orgId)
  const people = linkedOfKind(state, orgId, 'person')
  const deals = dealsForOrg(state, orgId)
  const facts = engagementFacts(state, config, orgId)
  const connections = connectionsForOrg(state, orgId)

  return (
    <Page
      // No crumb: this IS the top of the chain, and the Clients tab is lit and
      // clickable above it. A "Clients / " crumb here only repeated the tab.
      // "This deployment" rather than "Client" when the record is the team's own.
      // The page is otherwise the same page — internal work is a client record like
      // any other — so this one word is the whole difference, and it is read-only:
      // which org is us is set in Settings, because it decides who can sign in.
      eyebrow={isUs ? 'This deployment' : 'Client'}
      eyebrowIcon="Building2"
      // Editable, like every other record's title. This page renders the org's own
      // record and was the only one in the app where the name, the description and
      // the state were read-only — /e/org_* redirects here, so there was nowhere
      // else to go and change them.
      title={
        <EditableText
          value={fieldStr(org, 'name')}
          placeholder="Client name"
          onSave={(name: string) =>
            void dispatch({ type: 'org.update', subject: orgId, data: { name } })
          }
          style={{ marginLeft: -8 }}
        />
      }
      // The client link used to sit here too, minting on one screen and being copied
      // and revoked on another — two controls for one thing, on a page that already
      // has a tab about exactly that thing. It lives in Client view now.
      right={
        /* Every other record can be archived from its own page. An org's page is
           this one — /e/org_* redirects here — so without this a client was the one
           record type you could only put away from the list it was in. */
        <ConfirmAction
          title="Archive this client"
          confirmLabel="Archive"
          onConfirm={() => {
            void dispatch({ type: 'org.archive', subject: orgId })
            navigate('/orgs')
          }}
        />
      }
      banner={
        <>
          {org.archived && (
            <div
              className="rounded-control text-body mb-2 flex items-center gap-2 border px-3 py-1.5"
              style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
            >
              <Icon name="Archive" size={14} style={{ color: 'var(--muted)' }} />
              <span>This client is archived.</span>
              <button
                type="button"
                className="ml-auto font-medium"
                style={{ color: 'var(--accent)' }}
                onClick={() => void dispatch({ type: 'org.restore', subject: orgId })}
              >
                Restore
              </button>
            </div>
          )}
          <SubTabs
            tabs={[
              { key: 'overview', label: 'Overview' },
              { key: 'projects', label: 'Projects', count: projects.length },
              { key: 'artifacts', label: 'Artifacts', count: artifacts.length },
              { key: 'deals', label: 'Deals', count: deals.length },
              { key: 'people', label: 'People', count: people.length },
              { key: 'ecosystem', label: 'Ecosystem', count: connections.length },
              { key: 'timeline', label: 'Timeline' },
              { key: 'client', label: 'Client view', apart: true },
            ]}
            active={tab}
            onPick={(k) => setTab(k as TabKey)}
          />
        </>
      }
      // The client's own document gets the full width: it is a preview of something
      // that has no rail, and framing it next to an aside of internal fields would
      // undercut the one thing the tab is for.
      aside={tab === 'client' ? undefined : <ClientAside org={org} people={people} facts={facts} />}
    >
      {tab === 'overview' && (
        <Overview org={org} deals={deals} projects={projects} onTab={setTab} />
      )}
      {tab === 'projects' && <ProjectsTab org={org} projects={projects} />}
      {tab === 'artifacts' && <ArtifactsTab org={org} artifacts={artifacts} />}
      {tab === 'deals' && <DealsTab org={org} deals={deals} />}
      {tab === 'people' && <PeopleTab org={org} people={people} />}
      {tab === 'ecosystem' && <EcosystemTab org={org} />}
      {tab === 'timeline' && (
        <Section title="Timeline" note="notes, calls and decisions">
          <EntryThread subjectId={orgId} />
        </Section>
      )}
      {tab === 'client' && <ClientView orgId={orgId} projects={projects} artifacts={artifacts} />}
    </Page>
  )
}

/**
 * What is running right now, what is coming, and what was said.
 *
 * "In flight" is the one place in the app that mixes money and work on purpose.
 * That interleaving IS the state of an engagement, and no screen showed it: the
 * deals were in a rail and the boards were in a list, so you had to hold the two
 * together in your head.
 */
function Overview({
  org,
  deals,
  projects,
  onTab,
}: {
  org: EntityRecord
  deals: EntityRecord[]
  projects: EntityRecord[]
  onTab: (t: TabKey) => void
}) {
  const { state, config, dispatch } = useStore()
  const create = useCreateEntity()
  const orgId = org.id

  const kindOf = (d: EntityRecord) =>
    config.stages.find((st) => st.key === stageOf(d, config.stages))?.kind
  const running = deals.filter((d) => kindOf(d) === 'delivery' || kindOf(d) === 'won')
  const open = deals.filter((d) => kindOf(d) === 'open')
  const milestones = milestonesForWorks(
    state,
    projects.map((p) => p.id),
  ).slice(0, 4)

  return (
    <>
      {/* The client's own words. This page renders the org record and was the only
          one in the app with no way to edit its description: it was drawn as the
          header's sub-line, which is display-only everywhere. Every other kind has
          this section, called the same thing, in the same place. */}
      <Section title="Description">
        <RichField
          value={descriptionOf(org)}
          placeholder="Who they are, and what we do for them."
          onSave={(v) =>
            void dispatch({ type: 'org.update', subject: org.id, data: { description: v } })
          }
        />
      </Section>

      {/* Deals, and only deals. This was "In flight": deals and boards interleaved
          in one table, on the theory that money and work together IS the state of
          an engagement. The theory was fine and the table was not — a deal's STAGE
          and a project's TYPE are different things sharing one column, so the
          labels had different widths and the names never lined up. Two record
          types in one list also means no column can mean one thing. Projects have
          a tab; this section names what it shows. */}
      <Section
        title="Deals"
        note="idea through delivered"
        right={
          <span className="flex items-center gap-3">
            <button type="button" onClick={() => onTab('deals')} className="meta underline">
              all
            </button>
            <AddRecord
              kind="deal"
              exclude={deals.map((d) => d.id)}
              onPick={(id) =>
                void dispatch({ type: 'link.add', subject: org.id, data: { to: id } })
              }
              onCreate={(name) => void create('deal', name, { org: org.id }, { links: [org.id] })}
            />
          </span>
        }
      >
        {deals.length === 0 ? (
          <p className="note py-2">
            No deals yet. A deal starts at Idea, so "what if we sold X to them" has somewhere to
            land.
          </p>
        ) : (
          <div className="rows">
            {[...running, ...open].slice(0, 5).map((d) => (
              <DealRow key={d.id} deal={d} />
            ))}
          </div>
        )}
      </Section>

      <Section
        title="Coming up"
        // "milestones, not items" was a note to whoever built this: it recorded the
        // rule that item dates are excluded, which is a rule about the code and
        // means nothing on screen — you cannot tell what was left out of a list you
        // are seeing for the first time. The rule itself is still enforced and is
        // written at the top of this file, where rules belong. What the line says
        // now is where these dates came from, so you can go and look.
        note="dated groups on this client's boards"
        right={
          <button type="button" onClick={() => onTab('projects')} className="meta underline">
            Projects
          </button>
        }
      >
        {milestones.length === 0 ? (
          <p className="note py-2">
            Nothing dated yet. A group on a board carries the date you promised.
          </p>
        ) : (
          <div className="rows">
            {milestones.map((m) => {
              const board = state.entities[m.work]
              return (
                // The rows were plain divs, so the one question they raise — what is
                // this on? — had no answer you could click. They go to the board the
                // group is a bucket on.
                <Link
                  key={m.id}
                  to={`/b/${m.work}`}
                  className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-2.5"
                >
                  <span
                    className="num text-body shrink-0 font-semibold"
                    style={{ color: dueTone(m.due), minWidth: '6rem' }}
                  >
                    {m.due}
                  </span>
                  <span className="text-body min-w-[10rem] flex-1 font-medium">
                    {m.label}
                    <span className="meta block">
                      {board ? fieldStr(board, 'name') : 'project since removed'}
                    </span>
                  </span>
                  <Icon name="ArrowRight" size={14} style={{ color: 'var(--faint)' }} />
                </Link>
              )
            })}
          </div>
        )}
      </Section>

      {/* The composer lives here, not only on the Timeline tab. Logging a call is
          what you open a client for, and it must not cost a click. */}
      <Section title="Lately" note="notes, calls and decisions">
        <EntryThread subjectId={orgId} limit={3} onMore={() => onTab('timeline')} />
      </Section>
    </>
  )
}

/** One deal, as a row: stage, name, value. Used twice on Overview and once per tab. */
function DealRow({ deal }: { deal: EntityRecord }) {
  const { state, config } = useStore()
  const stage = config.stages.find((st) => st.key === stageOf(deal, config.stages))
  const value = Number(deal.fields.value) || 0
  return (
    <Link to={`/e/${deal.id}`} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-2.5">
      {stage && (
        <span
          className="eyebrow shrink-0"
          style={{ color: stageTone(stage.kind) ?? 'var(--ember)', minWidth: '5.5rem' }}
        >
          {stage.label}
        </span>
      )}
      <span className="text-body min-w-[10rem] flex-1 font-medium">
        {titleOf(state, deal.id) || 'Untitled'}
      </span>
      <span className="num text-meta shrink-0" style={{ color: 'var(--muted)' }}>
        {value ? money(value, '·') : '·'}
      </span>
      <Icon name="ArrowRight" size={14} style={{ color: 'var(--faint)' }} />
    </Link>
  )
}

/** Running work, then what it produced. The same question a week apart, so one tab. */
/** Running work. One type, named after it. */
function ProjectsTab({ org, projects }: { org: EntityRecord; projects: EntityRecord[] }) {
  const { state, config, dispatch } = useStore()
  const create = useCreateEntity()
  return (
    <>
      <Section
        title="Projects"
        note={projects.length === 1 ? '1' : `${projects.length}`}
        // Linked to this client on creation, which is the only reason adding it
        // from here beats the New button on the Projects list.
        right={
          <AddRecord
            kind="project"
            exclude={projects.map((w) => w.id)}
            onPick={(id) => void dispatch({ type: 'link.add', subject: org.id, data: { to: id } })}
            onCreate={(name) => void create('project', name, undefined, { links: [org.id] })}
          />
        }
      >
        {projects.length === 0 ? (
          <p className="note py-2">
            Nothing running. A project becomes a board the moment you put a group on it.
          </p>
        ) : (
          <div className="rows">
            {projects.map((b) => {
              const counts = shownCount(state, b.id)
              const next = groupsForWork(state, b.id).filter((g) => g.due)[0]
              const open = itemsForWork(state, b.id).filter((i) => i.status !== 'done').length
              const board = isBoard(state, b.id)
              const kind = labelOf(config.projectKinds, fieldStr(b, 'kind'))
              return (
                <Link
                  key={b.id}
                  to={`/e/${b.id}`}
                  className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-2.5"
                >
                  <span className="eyebrow shrink-0" style={{ minWidth: '5.5rem' }}>
                    {kind?.label ?? '·'}
                  </span>
                  <span className="text-body min-w-[10rem] flex-1 font-medium">
                    {fieldStr(b, 'name')}
                  </span>
                  <span className="meta shrink-0">
                    {board
                      ? `${open} open · ${counts.shown} of ${counts.total} shared`
                      : 'no board'}
                  </span>
                  <span
                    className="num text-meta shrink-0"
                    style={{ color: next ? dueTone(next.due) : 'var(--faint)' }}
                  >
                    {next ? next.due : '·'}
                  </span>
                </Link>
              )
            })}
          </div>
        )}
      </Section>
    </>
  )
}

/** What the work produced. */
function ArtifactsTab({ org, artifacts }: { org: EntityRecord; artifacts: EntityRecord[] }) {
  const { config, dispatch } = useStore()
  const create = useCreateEntity()
  return (
    <>
      <Section
        title="Artifacts"
        note={artifacts.length === 1 ? '1' : `${artifacts.length}`}
        right={
          <AddRecord
            kind="artifact"
            exclude={artifacts.map((a) => a.id)}
            onPick={(id) => void dispatch({ type: 'link.add', subject: org.id, data: { to: id } })}
            onCreate={(name) => void create('artifact', name, undefined, { links: [org.id] })}
          />
        }
      >
        {artifacts.length === 0 ? (
          <p className="meta py-2">Nothing shipped yet.</p>
        ) : (
          <div className="rows">
            {artifacts.map((a) => (
              <Link
                key={a.id}
                to={`/e/${a.id}`}
                className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2.5"
              >
                <span className="eyebrow shrink-0" style={{ minWidth: '5.5rem' }}>
                  {labelOf(config.artifactKinds, fieldStr(a, 'kind'))?.label ?? '·'}
                </span>
                <span className="text-body min-w-0 flex-1">{fieldStr(a, 'name')}</span>
                <span className="num text-meta">{fieldStr(a, 'delivered') || '·'}</span>
              </Link>
            ))}
          </div>
        )}
      </Section>
    </>
  )
}

function DealsTab({ org, deals }: { org: EntityRecord; deals: EntityRecord[] }) {
  const create = useCreateEntity()
  return (
    <Section
      title="Deals"
      note="idea through delivered"
      right={
        <button
          type="button"
          onClick={() =>
            void create('deal', `${fieldStr(org, 'name')} — new deal`, { org: org.id })
          }
          className="meta underline"
        >
          new
        </button>
      }
    >
      {deals.length === 0 ? (
        <p className="meta py-2">No deals yet.</p>
      ) : (
        <div className="rows">
          {deals.map((d) => (
            <DealRow key={d.id} deal={d} />
          ))}
        </div>
      )}
    </Section>
  )
}

function PeopleTab({ org, people }: { org: EntityRecord; people: EntityRecord[] }) {
  const { dispatch } = useStore()
  const create = useCreateEntity()
  return (
    <Section
      title="People"
      note={`${people.length}`}
      right={
        <AddRecord
          kind="person"
          exclude={people.map((p) => p.id)}
          onPick={(id) => void dispatch({ type: 'link.add', subject: org.id, data: { to: id } })}
          onCreate={(name) => void create('person', name, undefined, { links: [org.id] })}
        />
      }
    >
      {people.length === 0 ? (
        <p className="meta py-2">Nobody linked yet.</p>
      ) : (
        <div className="rows">
          {people.map((p) => (
            <Link key={p.id} to={`/e/${p.id}`} className="flex items-center gap-3 py-2.5">
              <Avatar actor={{ id: p.id, name: fieldStr(p, 'name') || '?' }} size={20} />
              <span className="text-body min-w-[10rem] flex-1 font-medium">
                {fieldStr(p, 'name')}
              </span>
              <span className="meta">{fieldStr(p, 'role') || '·'}</span>
              <span className="meta">{fieldStr(p, 'email')}</span>
            </Link>
          ))}
        </div>
      )}
    </Section>
  )
}

/**
 * What the client opens, shown inside the app.
 *
 * An IFRAME of the real `/r/<token>`, not a shared component. ClientSeat
 * deliberately never touches the store — the payload arrives already filtered from
 * `/api/client/:token` — and that isolation is the thing stopping a future
 * refactor leaking the whole log outward. Rendering the real URL keeps it, and has
 * the side benefit that what you are looking at is literally what they get rather
 * than a second implementation that can drift.
 *
 * The bar above it answers the question you actually have before sending a link,
 * including the negative. It has to stay true: it once said "no internal notes"
 * after the note had started travelling with the row, which is the one kind of
 * wrong a bar like this cannot be.
 */
function ClientView({
  orgId,
  projects,
  artifacts,
}: {
  orgId: string
  projects: EntityRecord[]
  artifacts: EntityRecord[]
}) {
  const { state } = useStore()
  const [token, setToken] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    let live = true
    void api
      .shares()
      .then(({ shares }) => {
        const hit = Object.entries(shares).find(([, s]) => s.org === orgId)
        if (live) {
          setToken(hit ? hit[0] : null)
          setLoaded(true)
        }
      })
      .catch(() => live && setLoaded(true))
    return () => {
      live = false
    }
  }, [orgId])

  const counts = projects.reduce(
    (acc, p) => {
      const c = shownCount(state, p.id)
      return {
        shown: acc.shown + c.shown,
        total: acc.total + c.total,
        boards: acc.boards + (c.total > 0 ? 1 : 0),
        shownBoards: acc.shownBoards + (c.shown > 0 ? 1 : 0),
      }
    },
    { shown: 0, total: 0, boards: 0, shownBoards: 0 },
  )

  if (!loaded) return <p className="meta py-6">Checking for a link…</p>

  if (!token) {
    return (
      <EmptyState
        icon="Link"
        title="No client link yet"
        hint="Minting a link is not publishing. What they see is still decided row by row on the boards; this only decides whether there is a door at all."
        action={
          <Button
            variant="work"
            onClick={() => {
              setBusy(true)
              void api
                .share(orgId)
                .then((r) => setToken(r.token))
                .finally(() => setBusy(false))
            }}
          >
            {busy ? 'Making…' : 'Make a client link'}
          </Button>
        }
      />
    )
  }

  return (
    <div>
      <div
        className="rounded-control mb-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 border px-3 py-2"
        style={{
          borderColor: 'color-mix(in srgb, var(--marine) 35%, transparent)',
          background: 'color-mix(in srgb, var(--marine) 7%, transparent)',
        }}
      >
        <span className="eyebrow" style={{ color: 'var(--marine)' }}>
          What they see
        </span>
        <span className="text-meta" style={{ color: 'var(--muted)' }}>
          {counts.shown} of {counts.total} rows · {counts.shownBoards} of {counts.boards} boards ·{' '}
          {artifacts.length} artifacts · the note and thread on each of those rows · no deals, no
          values
        </span>
        {/* The link itself, where the tab about the link is. Minting it is the empty
            state above; copying and withdrawing it belong beside the preview of what
            it opens, not in the page header two tabs away. */}
        <span className="ml-auto flex flex-wrap items-center gap-2.5">
          <a
            href={clientPageUrl(token)}
            target="_blank"
            rel="noreferrer"
            className="meta underline"
          >
            open as them
          </a>
          <button
            type="button"
            title={`${location.origin}${clientPageUrl(token)}`}
            onClick={() => {
              void navigator.clipboard?.writeText(`${location.origin}${clientPageUrl(token)}`)
              setCopied(true)
              window.setTimeout(() => setCopied(false), 1400)
            }}
            className="rounded-control text-meta border px-2.5 py-1"
            style={{
              borderColor: 'color-mix(in srgb, var(--good) 45%, transparent)',
              color: 'var(--good)',
              background: 'var(--panel)',
            }}
          >
            {copied ? 'Copied' : '● Copy client link'}
          </button>
          {/* Revoking kills the link for everyone holding it, immediately — so it
              asks first, which the header's bare `revoke` never did. */}
          <ConfirmAction
            icon="X"
            title="Revoke this client link"
            confirmLabel="Revoke"
            onConfirm={() => void api.revokeShare(token).then(() => setToken(null))}
          />
        </span>
      </div>

      <iframe
        src={clientPageUrl(token)}
        title="The client's view"
        className="rounded-control w-full border"
        style={{ height: '70vh', borderColor: 'var(--border)', background: 'var(--panel)' }}
      />
    </div>
  )
}

/**
 * The rail: the client's own fields and who we know there.
 *
 * The deals moved out. They were here because the page had nowhere else to put the
 * commercial relationship, and now they have a tab of their own and a place in
 * "In flight" — which is where you actually look for them.
 */
function ClientAside({
  org,
  people,
  facts,
}: {
  org: EntityRecord
  people: EntityRecord[]
  facts: EngagementFacts
}) {
  const { dispatch } = useStore()
  const create = useCreateEntity()
  const upd = (key: string, val: string | number) =>
    void dispatch({ type: 'org.update', subject: org.id, data: { [key]: val } })

  // Only the fields the header does not already carry: the name is the title, the
  // description is the subtitle.
  const fields = KINDS.org.fields.filter((f) => !['name', 'description'].includes(f.key))

  return (
    <>
      {/* These were a strip of tiles across the top of the page. They are derived
          numbers, not prose, and a row of boxes that wide read as furniture rather
          than as facts — so they sit with the other facts instead. Nothing here is
          conditional: a new client shows dashes and "never", which is still an
          answer and is the thing the old page could not say at all. */}
      <AsideBlock title="Engagement">
        <div className="flex flex-wrap gap-x-6 gap-y-1">
          <Fact label="In delivery" hint={`${facts.deliveryCount || 'no'} running`}>
            <span className="num" style={{ color: facts.inDelivery ? 'var(--ember)' : undefined }}>
              {facts.inDelivery ? money(facts.inDelivery, '·') : '·'}
            </span>
          </Fact>
          <Fact label="Pipeline" hint={`${facts.pipelineCount || 'no'} out`}>
            <span className="num">{facts.pipeline ? money(facts.pipeline, '·') : '·'}</span>
          </Fact>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-1">
          <Fact
            label="Open items"
            hint={`on ${facts.boardCount || 'no'} board${facts.boardCount === 1 ? '' : 's'}`}
          >
            <span className="num">{facts.openItems}</span>
          </Fact>
          <Fact
            label="Last spoke"
            hint={facts.lastContact ? formatDate(facts.lastContact) : 'nothing logged'}
          >
            <span className="num">
              {facts.lastContact ? relativeShort(facts.lastContact) : 'never'}
            </span>
          </Fact>
        </div>
        <Fact label="Next date" hint={facts.next ? facts.next.label : 'nothing dated'}>
          {facts.next ? (
            <Link
              to={`/b/${facts.next.work}`}
              className="num"
              style={{ color: dueTone(facts.next.due) }}
            >
              {facts.next.due}
            </Link>
          ) : (
            <span className="num">·</span>
          )}
        </Fact>
      </AsideBlock>

      <AsideBlock title="Details">
        {fields.map((f) => (
          <AsideField key={f.key} def={f} rec={org} onSave={(v) => upd(f.key, v)} />
        ))}
      </AsideBlock>

      <AsideBlock
        title={`People · ${people.length}`}
        right={
          <AddRecord
            kind="person"
            label="Add"
            exclude={people.map((p) => p.id)}
            onPick={(id) => void dispatch({ type: 'link.add', subject: org.id, data: { to: id } })}
            onCreate={(name) =>
              void create('person', name, undefined, { links: [org.id], stay: true })
            }
          />
        }
      >
        {people.length === 0 ? (
          <p className="meta py-1">Nobody linked yet.</p>
        ) : (
          // A list, not a row of chips. Chips read as tags — as labels ON the
          // client rather than people you can open — and they carried none of the
          // avatar colouring the People list and every timeline entry already use
          // for the same person. Same person, same mark, everywhere.
          <div className="rows">
            {people.map((p) => (
              <Link key={p.id} to={`/e/${p.id}`} className="flex items-center gap-2 py-1.5">
                <Avatar actor={{ id: p.id, name: fieldStr(p, 'name') || '?' }} size={20} />
                <span className="text-body min-w-0 flex-1 truncate">{fieldStr(p, 'name')}</span>
                {fieldStr(p, 'role') && (
                  <span className="meta shrink-0">{fieldStr(p, 'role')}</span>
                )}
              </Link>
            ))}
          </div>
        )}
      </AsideBlock>
    </>
  )
}
